import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { ClubProfileTabIdentifiers } from '../../../../utils/constants';
import { useCPPDynamicConfig } from '../../../hooks/useCPPDynamicConfig';
import useCPPTracking from '../../../hooks/useCPPTracking';
import { CppCoachActions, CppCoachSurfaces } from '../../../Analytics/constants';
import { useStatsig } from '../../../hooks/useStatsig';
import StatsigLink from '../../../Links/StatsigLink';
import config from '../../../../../config';
import styles from '../headerV3.scss';

const RECRUITS_ENDPOINT = '/v1/cpp/recruits';

const COACH_TEAM_GENDERS_BOTH = 'both';
const COACH_TEAM_GENDERS_MEN = 'men';
const COACH_TEAM_GENDERS_WOMEN = 'women';

const PLAYER_GENDER_M = 'M';
const PLAYER_GENDER_MALE = 'MALE';
const PLAYER_GENDER_F = 'F';
const PLAYER_GENDER_FEMALE = 'FEMALE';

const CPP_TEAM_GENDER_MALE = 'Male';
const CPP_TEAM_GENDER_FEMALE = 'Female';
const CPP_TEAM_GENDER_MIXED = 'Mixed';

const coachTeamGendersMatchPlayer = (coachTeamGenders, playerGender) => {
    if (!coachTeamGenders || typeof playerGender !== 'string') {
        return false;
    }
    if (coachTeamGenders === COACH_TEAM_GENDERS_BOTH) {
        return true;
    }
    const normalized = playerGender.trim().toUpperCase();
    if (normalized === PLAYER_GENDER_M || normalized === PLAYER_GENDER_MALE) {
        return coachTeamGenders === COACH_TEAM_GENDERS_MEN;
    }
    if (normalized === PLAYER_GENDER_F || normalized === PLAYER_GENDER_FEMALE) {
        return coachTeamGenders === COACH_TEAM_GENDERS_WOMEN;
    }
    return false;
};

// CPP coaches are TeamRole-based and have a null legacy coachTeamGenders string.
// The API returns their coached genders in the collegeCoachTeamGenders array instead
// (values: "Male" / "Female" / "Mixed").
const collegeCoachTeamGendersMatchPlayer = (collegeCoachTeamGenders, playerGender) => {
    if (!collegeCoachTeamGenders || !collegeCoachTeamGenders.length || typeof playerGender !== 'string') {
        return false;
    }
    if (collegeCoachTeamGenders.includes(CPP_TEAM_GENDER_MIXED)) {
        return true;
    }
    const normalized = playerGender.trim().toUpperCase();
    if (normalized === PLAYER_GENDER_M || normalized === PLAYER_GENDER_MALE) {
        return collegeCoachTeamGenders.includes(CPP_TEAM_GENDER_MALE);
    }
    if (normalized === PLAYER_GENDER_F || normalized === PLAYER_GENDER_FEMALE) {
        return collegeCoachTeamGenders.includes(CPP_TEAM_GENDER_FEMALE);
    }
    return false;
};

const CollegeCoachAddProspect = ({ profileData, playerId, isOwnProfile, 'data-testid': dataTestId }) => {
    const dispatch = useDispatch();
    const authUser = useSelector((state) => state.auth?.user);
    const { featureGates } = useStatsig();
    const { isCollegeProspectPowerEnabled } = useCPPDynamicConfig();
    const { trackCoachEngagement } = useCPPTracking();

    const [isAdded, setIsAdded] = useState(!!profileData?.alreadyRecruit);
    const [isSaving, setIsSaving] = useState(false);

    const targetMemberId = profileData?.memberId;
    const isEligibleCoach = !!authUser?.isCollegeCoachCppEligible;
    const gendersMatch = coachTeamGendersMatchPlayer(authUser?.coachTeamGenders, profileData?.gender) ||
        collegeCoachTeamGendersMatchPlayer(authUser?.collegeCoachTeamGenders, profileData?.gender);

    const collegeClub = authUser?.clubMemberships?.find((m) => {
        const tier = m?.tierTypeName?.toLowerCase();
        return tier === 'pro college' || tier === 'free college';
    });
    const coachClubId = collegeClub?.clubId;

    if (!isCollegeProspectPowerEnabled || isOwnProfile || !isEligibleCoach || !targetMemberId || !gendersMatch) {
        return null;
    }

    const handleAddClick = async () => {
        setIsSaving(true);
        try {
            await getApiAxiosInstance().post(RECRUITS_ENDPOINT, { targetMemberId });
            setIsAdded(true);
            trackCoachEngagement(
                CppCoachActions.CPP_COACH_PROSPECT_LIKED,
                CppCoachSurfaces.PLAYER_PROFILE,
                // ENG-2735: canonical `player_id`, plus `coachClubId` as the
                // cpp_coach_school_id fallback — this banner already resolved
                // the coach's college club above to decide whether to render.
                { player_id: playerId, cpp_coach_school_id: coachClubId ?? null },
            );
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t add this player to your prospects. Please try again.',
                error: err,
            }));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.coachProspectRow} data-testid={dataTestId}>
            <span
                className={styles.coachProspectLabel}
                data-testid={dataTestId && `${dataTestId}.label`}
            >
                For College Coaches
            </span>
            {isAdded ? (
                <StatsigLink
                    gate={featureGates.CLUB_LINKS_REDIRECT}
                    enabledTo={coachClubId ? `${config.nextAppHost}/college/${coachClubId}?tab=prospects` : '#'}
                    disabledTo={coachClubId ? `/clubs/${coachClubId}?t=${ClubProfileTabIdentifiers.COLLEGE_CORNER}` : '#'}
                    className={styles.coachProspectAddedLink}
                    data-testid={dataTestId && `${dataTestId}.added-link`}
                >
                    <svg className={styles.coachProspectCheckIcon} viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span data-testid={dataTestId && `${dataTestId}.added-link.text`}>
                        Added to &ldquo;Prospects I&rsquo;m Interested In&rdquo;
                    </span>
                </StatsigLink>
            ) : (
                <button
                    type="button"
                    className={styles.coachProspectAddBtn}
                    onClick={handleAddClick}
                    disabled={isSaving}
                    data-testid={dataTestId && `${dataTestId}.add-btn`}
                >
                    <svg className={styles.coachProspectPlusIcon} viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12 7v10M7 12h10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    <span data-testid={dataTestId && `${dataTestId}.add-btn.text`}>
                        Add to &ldquo;Prospects I&rsquo;m Interested In&rdquo;
                    </span>
                </button>
            )}
        </div>
    );
};

CollegeCoachAddProspect.propTypes = {
    profileData: PropTypes.shape({
        memberId: PropTypes.number,
        gender: PropTypes.string,
    }),
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOwnProfile: PropTypes.bool,
    'data-testid': PropTypes.string,
};

CollegeCoachAddProspect.defaultProps = {
    profileData: null,
    playerId: null,
    isOwnProfile: false,
    'data-testid': null,
};

export default CollegeCoachAddProspect;
